import { useEffect } from "react";

function hasAnyRadixOverlayOpen() {
  // Radix components (Select/Dropdown/Dialog/Popover/etc.) commonly mark open content with data-state="open".
  // If something is genuinely open, we should NOT undo pointer-events protection.
  return Boolean(
    document.querySelector(
      "[data-radix-portal] [data-state='open'], [data-state='open'][role='dialog'], [data-state='open'][data-side]",
    ),
  );
}

function clearPointerEventsLock() {
  document.body.style.removeProperty("pointer-events");
  document.documentElement.style.removeProperty("pointer-events");
}

/**
 * Guards against a Radix edge case where the page becomes unclickable (pointer-events: none)
 * after certain popover/select interactions.
 */
export function useRadixPointerEventsGuard() {
  useEffect(() => {
    const maybeFix = () => {
      const bodyPE = getComputedStyle(document.body).pointerEvents;
      const htmlPE = getComputedStyle(document.documentElement).pointerEvents;
      const locked = bodyPE === "none" || htmlPE === "none";
      if (!locked) return;
      if (hasAnyRadixOverlayOpen()) return;
      clearPointerEventsLock();
    };

    // Initial pass
    maybeFix();

    // Watch style attribute mutations (where this bug tends to show up)
    const bodyObserver = new MutationObserver(() => {
      requestAnimationFrame(maybeFix);
    });
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ["style", "class"] });

    const htmlObserver = new MutationObserver(() => {
      requestAnimationFrame(maybeFix);
    });
    htmlObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class"] });

    // Also re-check when the tab regains focus
    const onVisibility = () => requestAnimationFrame(maybeFix);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      bodyObserver.disconnect();
      htmlObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, []);
}
